import Link from "next/link";
import { Typography, theme, Divider } from "antd";
import { isSystemField, columnInfo } from "@vzbl/integrations";
import { durationReadable } from "src/utils/date";
import { useIntegrationConfig } from "src/search/lib/useIntegrationConfig";
import { useApplicationContext } from "src/hooks/useApplication";
import { mono } from "src/fonts";
import { Flex } from "src/components/Flex";
import { attributeToTitle, formatValue } from "src/utils/strings";
import { ValueFormatter } from "src/utils/formatters";
import { VzblTag } from "../../components/VzblTag";
import { useEntityContext } from "../../useEntityContext";
import { EntityLink } from "../../components/EntityLink";

export type EntityProp = {
  label: string;
  child: React.ReactElement;
};

const ALWAYS_SKIPPED = [
  "filesAdded",
  "filesDeleted",
  "filesModified",
  "diffData",
];

const LABEL_WIDTH = 176;

export const EntityProperties = (props: {
  entity: any;
  skipKeys?: string[];
  systemInfo?: boolean;
}) => {
  const dateKeys: string[] = [];
  const analysisKeys: string[] = [];
  const systemKeys: string[] = [];
  const attributeKeys: string[] = [];
  const skipKeys = new Set([...(props.skipKeys || []), ...ALWAYS_SKIPPED]);

  Object.entries(props.entity.properties).forEach(([key, val]) => {
    if (skipKeys.has(key)) return;
    else if (isSystemField(key)) systemKeys.push(key);
    else if (key.startsWith("_")) systemKeys.push(key);
    else if (key.startsWith("vzbl")) analysisKeys.push(key);
    else if (/[a-z]+(At|Date)$/.test(key)) dateKeys.push(key);
    else if (key === "id") systemKeys.push(key);
    else if (/[a-z]+Id$/.test(key)) systemKeys.push(key);
    else if (typeof val === "string" && /\w{4,16}(-\w{4,16}){4,6}/.test(val))
      systemKeys.push(key);
    else attributeKeys.push(key);
  });

  dateKeys.sort((a, b) => {
    const dayA = new Date(props.entity.properties[a]).getTime();
    const dayB = new Date(props.entity.properties[b]).getTime();
    if (dayA < dayB) return -1;
    if (dayA > dayB) return 1;
    return a.toLowerCase().localeCompare(b.toLowerCase());
  });
  analysisKeys.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  attributeKeys.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  systemKeys.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

  return (
    <Flex direction="column" gap={32} start style={{ marginBottom: 40 }}>
      {props.systemInfo ? (
        <KeyValueSection keys={systemKeys} entity={props.entity} />
      ) : (
        <>
          <div>
            <KeyValueSection keys={attributeKeys} entity={props.entity} />
            <DateKeys keys={dateKeys} entity={props.entity} />
          </div>
          {analysisKeys.length > 0 && (
            <div style={{ width: "100%" }}>
              <span style={{ margin: 0, fontWeight: 400 }}>
                Additional analysis
              </span>
              <Divider style={{ marginTop: 4, marginBottom: 12 }} />
              <KeyValueSection keys={analysisKeys} entity={props.entity} />
            </div>
          )}
        </>
      )}
    </Flex>
  );
};

function DateKeys({ keys, entity }: { keys: string[]; entity: any }) {
  const { token } = theme.useToken();
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  const entityType = entity.labels[0];
  return keys.length > 0 ? (
    <Flex wrap start style={{ gap: "16px 0", paddingTop: 20 }} spaceBetween>
      {keys.map((key) => (
        <div
          key={key}
          style={{
            minWidth: 0,
            width: "49%",
            display: "flex",
          }}
        >
          <div
            style={{
              color: token.colorTextTertiary,
              width: LABEL_WIDTH,
              textAlign: "right",
              paddingRight: 16,
            }}
          >
            {attributeToTitle(key)}
          </div>
          <VzblTag
            asLink
            orgId={orgId}
            context={context}
            text={new Date(entity.properties[key]).toLocaleString()}
            linkParams={{
              orgId,
              entityType,
              attribute: key,
              value: entity.properties[key],
            }}
          />
        </div>
      ))}
    </Flex>
  ) : null;
}

function KeyValueSection({ keys, entity }: { keys: string[]; entity: any }) {
  const { token } = theme.useToken();
  return keys.length > 0 ? (
    <Flex wrap start direction="column" gap={16} style={{ width: "100%" }}>
      {keys.map((key) =>
        Array.isArray(entity.properties[key]) &&
        entity.properties[key].length === 0 ? null : (
          <div
            key={key}
            style={{ flex: 1, maxWidth: "100%", minWidth: 0, display: "flex" }}
          >
            <span
              style={{
                width: LABEL_WIDTH,
                display: "inline-block",
                color: token.colorTextTertiary,
                textAlign: "right",
                paddingRight: 16,
              }}
            >
              {attributeToTitle(key.replace(/^vzbl/, ""))}:
            </span>
            <span
              style={{
                display: "inline-block",
                color: token.colorTextSecondary,
                maxWidth: `calc(100% - ${LABEL_WIDTH}px)`,
              }}
            >
              <Value
                value={entity.properties[key]}
                entity={entity}
                attribute={key}
              />
            </span>
          </div>
        )
      )}
    </Flex>
  ) : null;
}

function Value({
  value,
  attribute,
  entity,
}: {
  entity: any;
  value: any;
  attribute: string;
}) {
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  const entityType = entity.labels[0];
  const integrationConfig = useIntegrationConfig();
  const info = columnInfo(
    `${entityType}.${attribute}`,
    integrationConfig.data as any
  );
  const unit = info?.unit;
  const type = info?.type;
  if (type === "string[]" && Array.isArray(value))
    return (
      <Flex start wrap gap={4}>
        {value.map((key, i) => (
          <Flex start key={`${key}-${i}`}>
            <VzblTag
              asLink
              orgId={orgId}
              context={context}
              text={key}
              linkParams={{ orgId, entityType, attribute, value: key }}
            />
            {i < value.length - 1 && <span>,</span>}
          </Flex>
        ))}
      </Flex>
    );
  if (Array.isArray(value) && typeof value === "object")
    return (
      <Flex start wrap gap={4}>
        {value.map((item, i) => (
          <Flex start key={`${item.uuid || item.value}-${i}`}>
            <EntityLink uuid={item.uuid} style={{ display: "flex" }}>
              <div className="trunc">{item.value}</div>
            </EntityLink>
            {i < value.length - 1 && <span>,</span>}
          </Flex>
        ))}
      </Flex>
    );
  if (type === "string[]" && typeof value === "string")
    return (
      <Flex start wrap gap={4}>
        {value.split(",").map((key, i) => (
          <Flex start key={`${key}-${i}`}>
            <VzblTag
              asLink
              orgId={orgId}
              context={context}
              text={key}
              linkParams={{ orgId, entityType, attribute, value: key }}
            />
            {i < value.length - 1 && <span>,</span>}
          </Flex>
        ))}
      </Flex>
    );
  if (typeof value === "object")
    return (
      <EntityLink uuid={value.uuid} style={{ display: "flex" }}>
        <div className="trunc">{value.value}</div>
      </EntityLink>
    );
  if (type === "number" || typeof value === "number")
    return (
      <div className={mono.className}>
        <VzblTag
          asLink
          orgId={orgId}
          context={context}
          text={
            type
              ? formatValue(value, type, unit)
              : unit
              ? ValueFormatter.formatWithUnit(value, unit)
              : attribute.endsWith("Time")
              ? durationReadable(value, "millis")
              : value.toLocaleString()
          }
          linkParams={{ orgId, entityType, attribute, value: `${value}` }}
        />
      </div>
    );
  if (type === "boolean" || typeof value === "boolean")
    return (
      <div className={mono.className}>
        <VzblTag
          asLink
          orgId={orgId}
          context={context}
          text={`${value} ${value ? "✓" : "×"} `}
          linkParams={{ orgId, entityType, attribute, value: `${value}` }}
        />
      </div>
    );
  if (type === "string" || typeof value === "string")
    return value.startsWith("{") || value.startsWith("[") ? (
      <div
        style={{
          height: 15,
          lineHeight: 1,
        }}
      >
        <Typography.Text copyable style={{ display: "flex" }}>
          <div className="trunc" style={{ flex: "1", width: "100%" }}>
            {value}
          </div>
        </Typography.Text>
      </div>
    ) : value.startsWith("http") ? (
      <Link href={value} target="_blank">
        <Typography.Link
          copyable
          ellipsis
          style={{
            width: LABEL_WIDTH,
            display: "block",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          {value}
        </Typography.Link>
      </Link>
    ) : (
      <VzblTag
        asLink
        orgId={orgId}
        context={context}
        text={formatValue(value, type, unit)}
        linkParams={{ orgId, entityType, attribute, value }}
      />
    );
  return <>{JSON.stringify(value)}</>;
}
